import { useMemo, useState } from 'react';

import {
    buildSearchWhere,
    DEFAULT_PAGE_SIZE,
    type PagedQueryArg,
    type QueryWhereValue,
} from 'src/apiClients/queries';
import {
    DEFAULT_DATA_TABLE_QUERY_STATE,
    SortDirection,
    type DataTableCellValue,
    type DataTableQueryState,
    type DataTableSort,
} from 'src/components/data-table/DataTable.types';
import { useDebouncedValue } from 'src/hooks/use-debounced-value';

const SEARCH_DEBOUNCE_MS = 300;

export interface UseEntityListQueryState {
    queryState: DataTableQueryState;
    setQueryState: React.Dispatch<React.SetStateAction<DataTableQueryState>>;
    queryArg: PagedQueryArg;
}

export interface UseEntityListQueryStateOptions {
    searchColumns: readonly string[];
    initialSort: DataTableSort[];
    baseWhere?: Record<string, QueryWhereValue>;
    baseExtraParams?: Record<string, string>;
    clientSortFields?: ReadonlySet<string>;
    initialSearch?: string;
    uuidSearchColumns?: readonly string[];
    initialFilters?: Record<string, DataTableCellValue[]>;
}

/**
 * Centralizes the query-state plumbing every paged list page repeats:
 * controlled DataTable state, debounced search, and translation of
 * sorts/filters/search into the server-side `{ limit, order, where }` shape.
 */
export const useEntityListQueryState = ({
    searchColumns,
    initialSort,
    baseWhere,
    baseExtraParams,
    clientSortFields,
    initialSearch = '',
    uuidSearchColumns,
    initialFilters,
}: UseEntityListQueryStateOptions): UseEntityListQueryState => {
    const [queryState, setQueryState] = useState<DataTableQueryState>(() => ({
        ...DEFAULT_DATA_TABLE_QUERY_STATE,
        sorts: initialSort,
        search: initialSearch,
        filters: initialFilters ?? {},
    }));
    const debouncedSearch = useDebouncedValue(queryState.search, SEARCH_DEBOUNCE_MS);

    const queryArg = useMemo<PagedQueryArg>(() => {
        const order = queryState.sorts
            .filter((s) => !clientSortFields?.has(s.field))
            .map(
                (s) =>
                    [s.field, s.direction === SortDirection.DESC ? 'DESC' : 'ASC'] as [
                        string,
                        'ASC' | 'DESC',
                    ],
            );
        // Offset/limit pagination is only stable when the order is a *total*
        // ordering. Sorting on a non-unique column (e.g. `state`) lets the
        // server return tied rows in different relative orders per page, so the
        // same row can straddle a page boundary and appear in two pages (or be
        // skipped entirely). Append the primary key as a final tiebreaker to
        // guarantee a deterministic total order across pages.
        if (!order.some(([field]) => field === 'id')) {
            order.push(['id', 'ASC']);
        }
        const where: Record<string, QueryWhereValue> = { ...baseWhere };
        for (const [field, values] of Object.entries(queryState.filters)) {
            if (values.length === 0 || values.some((v) => v === '' || v == null)) {
                // Empty/null values can't be expressed in the server query string —
                // skip this field and let DataTable's client-side filtering handle it.
                continue;
            }
            where[field] = values.length === 1 ? (values[0] as QueryWhereValue) : values;
        }
        if (debouncedSearch) {
            where.$or = buildSearchWhere(searchColumns, debouncedSearch, uuidSearchColumns).$or;
        }
        return { limit: DEFAULT_PAGE_SIZE, order, where, extraParams: baseExtraParams };
    }, [
        queryState.sorts,
        queryState.filters,
        debouncedSearch,
        searchColumns,
        uuidSearchColumns,
        baseWhere,
        baseExtraParams,
        clientSortFields,
    ]);

    return { queryState, setQueryState, queryArg };
};
